/**
 * Spatial (directional) focus — the pure geometry half of TV remote navigation.
 *
 * A TV remote has no pointer, so "tab to the next thing" is not good enough: in
 * a grid of poster rails the viewer presses Right to advance along a rail and
 * Down to drop into the row below, and the focus has to land on the element that
 * is *spatially* closest in that direction. This is the same idea as the W3C
 * Spatial Navigation spec, reduced to what a media grid needs.
 *
 * No DOM here on purpose: `remote-nav.js` supplies rectangles, this module only
 * decides which rectangle wins, so the ranking rules are unit-testable in node
 * (scripts/test-remote-nav.mjs).
 */

/** Anything a remote can land on. Mirrors the query used by remote-nav.js. */
export const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export const DIRECTIONS = ["left", "right", "up", "down"];

/**
 * How much a candidate has to be "ahead" of the current item to count as being
 * in that direction at all. Without this, two elements that merely overlap (a
 * card and the row heading above it) would ping-pong focus between themselves.
 */
const MIN_ADVANCE = 2;

/**
 * Weight applied to sideways drift. Moving Right to a card one row down and 200px
 * to the right is almost never what the viewer meant, so crossing rows costs far
 * more than it saves; this is what makes rails behave like rails.
 */
const CROSS_DRIFT_PENALTY = 2.5;

/** Preference for staying in the same band, in the same units as the drift. */
const SAME_BAND_BONUS = 24;

/**
 * Distance between two 1-D ranges: 0 when they overlap or merely touch, and
 * otherwise the number of pixels of empty space between them. Note this is NOT
 * the signed overlap (min - max), which is negative when the ranges intersect —
 * feeding that into a score makes a candidate 600px further away look better than
 * the card directly below you.
 */
function rangeDistance(aStart, aEnd, bStart, bEnd) {
  return Math.max(0, Math.max(aStart, bStart) - Math.min(aEnd, bEnd));
}

function isVertical(dir) {
  return dir === "up" || dir === "down";
}

/**
 * Signed distance from `cur` to `cand` along the movement axis, measured edge to
 * edge. Negative (overlapping) values are clamped to 0, because an overlapping
 * neighbour is adjacent, not far away.
 */
function gapAlong(cur, cand, dir) {
  switch (dir) {
    case "right":
      return Math.max(0, cand.x - (cur.x + cur.w));
    case "left":
      return Math.max(0, cur.x - (cand.x + cand.w));
    case "down":
      return Math.max(0, cand.y - (cur.y + cur.h));
    default:
      return Math.max(0, cur.y - (cand.y + cand.h));
  }
}

/**
 * Distance between the two items on the axis perpendicular to travel, clamped at
 * zero. A negative value here would mean "the ranges overlap by n px", and
 * letting a negative number into the score *rewards* far-away candidates — Down
 * from the fourth card of a rail would then land on the leftmost card of the row
 * below, because that is 600px further away and so scores 1500 points better.
 */
function gapAcross(cur, cand, dir) {
  return isVertical(dir)
    ? rangeDistance(cand.x, cand.x + cand.w, cur.x, cur.x + cur.w)
    : rangeDistance(cand.y, cand.y + cand.h, cur.y, cur.y + cur.h);
}

/**
 * Accepts what getBoundingClientRect() actually returns and hands the ranker the
 * small shape it works in.
 *
 * This exists because of a real bug: the ranker reads {x, y, w, h}, but a DOMRect
 * spells those {left, top, width, height}. Mixed together, every distance comes
 * out NaN, no candidate ever scores better, and the remote silently does nothing
 * on a live page while hand-built fixtures in the tests pass happily. Normalise
 * here so the two shapes can never drift apart again.
 */
export function toRects(domRects) {
  return domRects.map((r) => ({
    x: r.left ?? r.x ?? 0,
    y: r.top ?? r.y ?? 0,
    w: r.width ?? r.w ?? 0,
    h: r.height ?? r.h ?? 0,
  }));
}

/**
 * Index of the element focus should move to, or -1 when that direction is a dead
 * end (e.g. Right on the last card of a rail — the caller then leaves focus put).
 *
 * @param {Array<{x:number,y:number,w:number,h:number}>} rects candidates in the
 *   active band, in DOM order; ties resolve to DOM order so movement is stable.
 * @param {number} from index of the currently focused rect.
 * @param {"left"|"right"|"up"|"down"} dir
 */
export function spatialNext(rects, from, dir) {
  if (!DIRECTIONS.includes(dir)) return -1;
  const cur = rects[from];
  if (!cur) return -1;

  const curCentre = isVertical(dir) ? cur.y + cur.h / 2 : cur.x + cur.w / 2;
  let best = -1;
  let bestScore = Infinity;

  for (let i = 0; i < rects.length; i += 1) {
    if (i === from) continue;
    const cand = rects[i];
    const candCentre = isVertical(dir) ? cand.y + cand.h / 2 : cand.x + cand.w / 2;

    // "Ahead" is measured on centres so that two partially overlapping items
    // (a wide card and the one peeking out from under it) still separate.
    const advance = dir === "right" || dir === "down" ? candCentre - curCentre : curCentre - candCentre;
    if (advance <= MIN_ADVANCE) continue;

    // Zero distance across means the two share a band (same row when moving
    // horizontally, same column when moving vertically).
    const across = gapAcross(cur, cand, dir);
    const sameBand = across === 0;

    const score =
      gapAlong(cur, cand, dir) +
      across * CROSS_DRIFT_PENALTY -
      (sameBand ? SAME_BAND_BONUS : 0);

    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  }

  return best;
}
